import { useToast, POSITION, type PluginOptions } from 'vue-toastification'
import { ref } from 'vue'

import type {
  LookupServiceProviderType,
  LookupServiceResponseType,
  PotaParkType,
  QsoType,
  SotaSummitType
} from '@/types'

import CallsignFoundToast from '@/components/toasts/CallsignFoundToast.vue'
import CallsignNotFoundToast from '@/components/toasts/CallsignNotFoundToast.vue'
import DuplicateQsoToast from '@/components/toasts/DuplicateQsoToast.vue'
import LookupProviderErrorToast from '@/components/toasts/LookupProviderErrorToast.vue'
import PotaParkFoundToastVue from '@/components/toasts/PotaParkFoundToast.vue'
import PotaSpotCreatedToastVue from '@/components/toasts/PotaSpotCreatedToast.vue'
import SotaSummitFoundToast from '@/components/toasts/SotaSummitFoundToast.vue'
import AdifExportErrorToast from '@/components/toasts/AdifExportErrorToast.vue'
import AdifExportPartialToast from '@/components/toasts/AdifExportPartialToast.vue'
import CabrilloExportErrorToast from '@/components/toasts/CabrilloExportErrorToast.vue'
import type { QsoConversionError } from '@/lib/AdifExporter'
import SyncErrorToast from '@/components/toasts/SyncErrorToast.vue'
import RigControlDisconnectedToast from '@/components/toasts/RigControlDisconnectedToast.vue'
import WatchlistMatchToast from '@/components/toasts/WatchlistMatchToast.vue'
import type { SpotPayload } from '@/services/PotaService'
import type { PotaSpotType, SotaSpotType } from '@/types'
import type { WatchlistRefKind } from '@/stores/WatchlistStore'

const toast = useToast()

// Track toast IDs for different categories to dismiss previous ones
const activeToastIds = ref({
  callsignFound: null as string | number | null,
  callsignNotFound: null as string | number | null,
  lookupProviderError: null as string | number | null,
  spotsConnectivity: null as string | number | null,
  rigControlStatus: null as string | number | null
})

const baseOptions: PluginOptions = {
  timeout: 8500,
  icon: false
}

/**
 * Dismisses a previous toast if it exists and updates the tracked ID
 */
function dismissPreviousToast(category: keyof typeof activeToastIds.value) {
  const previousToastId = activeToastIds.value[category]
  if (previousToastId !== null) {
    toast.dismiss(previousToastId)
    activeToastIds.value[category] = null
  }
}

export function useToastSystem() {
  function potaParkFoundToast(park: PotaParkType, position?: POSITION) {
    const content = {
      component: PotaParkFoundToastVue,
      props: {
        park: park
      }
    }
    toast.success(content, position ? { ...baseOptions, position } : baseOptions)
  }

  function sotaSummitFoundToast(summit: SotaSummitType) {
    const content = {
      component: SotaSummitFoundToast,
      props: {
        summit: summit
      }
    }
    toast.success(content, baseOptions)
  }

  function potaSpotCreatedToast(spotPayload: SpotPayload) {
    const content = {
      component: PotaSpotCreatedToastVue,
      props: {
        spotPayload: spotPayload
      }
    }
    toast.success(content, baseOptions)
  }

  function callsignFoundToast(lookup: LookupServiceResponseType, position?: POSITION) {
    // Dismiss previous callsign-related toasts
    dismissPreviousToast('callsignFound')
    dismissPreviousToast('callsignNotFound')
    dismissPreviousToast('lookupProviderError')

    const content = {
      component: CallsignFoundToast,
      props: {
        lookup: lookup
      }
    }
    const options = position ? { ...baseOptions, position } : baseOptions
    const toastId = toast.info(content, options)
    activeToastIds.value.callsignFound = toastId
  }

  function callsignNotFoundToast(callsign: string, provider: LookupServiceProviderType, position?: POSITION) {
    // Dismiss previous callsign-related toasts
    dismissPreviousToast('callsignFound')
    dismissPreviousToast('callsignNotFound')
    dismissPreviousToast('lookupProviderError')

    const content = {
      component: CallsignNotFoundToast,
      props: {
        callsign: callsign,
        provider: provider
      }
    }
    const options = position ? { ...baseOptions, position } : baseOptions
    const toastId = toast.error(content, options)
    activeToastIds.value.callsignNotFound = toastId
  }

  function lookupProviderErrorToast(provider: LookupServiceProviderType, error?: string, position?: POSITION) {
    // Dismiss previous callsign-related toasts
    dismissPreviousToast('callsignFound')
    dismissPreviousToast('callsignNotFound')
    dismissPreviousToast('lookupProviderError')

    const content = {
      component: LookupProviderErrorToast,
      props: {
        provider: provider,
        error: error
      }
    }
    const options = position ? { ...baseOptions, position } : baseOptions
    const toastId = toast.error(content, options)
    activeToastIds.value.lookupProviderError = toastId
  }

  function duplicateQsoToast(qso: QsoType, duplicateQso: QsoType) {
    const content = {
      component: DuplicateQsoToast,
      props: {
        qso,
        duplicateQso
      }
    }
    toast.error(content, baseOptions)
  }

  function adifExportErrorToast(error: string) {
    const content = {
      component: AdifExportErrorToast,
      props: {
        error: error
      }
    }
    // Dismiss-only - export errors are rare, often long, and the user usually
    // wants to read or screenshot them before they vanish.
    toast.error(content, { ...baseOptions, timeout: false })
  }

  function adifExportSuccessToast(path: string) {
    toast.success(`Export saved to ${path}`, baseOptions)
  }

  function adifExportPartialToast(failures: QsoConversionError[], totalQsos: number) {
    const content = {
      component: AdifExportPartialToast,
      props: {
        failures,
        totalQsos
      }
    }
    // Dismissible-only - the user must acknowledge that some QSOs were skipped.
    toast.warning(content, { ...baseOptions, timeout: false })
  }

  function adifExportCancelledToast(message: string) {
    toast.info(message, baseOptions)
  }

  function cabrilloExportErrorToast(error: string) {
    const content = {
      component: CabrilloExportErrorToast,
      props: {
        error: error
      }
    }
    // Dismiss-only - see adifExportErrorToast for rationale.
    toast.error(content, { ...baseOptions, timeout: false })
  }

  function syncErrorToast(error: string) {
    const content = {
      component: SyncErrorToast,
      props: {
        error: error
      }
    }
    // Toast but don't time out. Require them to dismiss it.
    toast.error(content, { ...baseOptions, timeout: false })
  }

  /**
   * Surface a rig-control disconnect/failure. Rig control is a non-essential
   * enhancement, so this is a non-blocking warning the user can ignore.
   *
   * All rig-control status toasts (connected/disconnected/reconnected/gave up)
   * share one slot: a flapping connection replaces its previous toast instead
   * of stacking a new one every cycle.
   */
  function rigControlDisconnectedToast(opts: { message?: string; detail?: string }) {
    dismissPreviousToast('rigControlStatus')
    const content = {
      component: RigControlDisconnectedToast,
      props: {
        message: opts.message,
        detail: opts.detail
      }
    }
    activeToastIds.value.rigControlStatus = toast.warning(content, baseOptions)
  }

  /**
   * Warn that spot fetches (POTA/SOTA) keep failing, so the spot list may be
   * stale. Sticky (no timeout) since it reflects an ongoing condition; cleared
   * by dismissSpotsConnectivityToast() once a fetch succeeds. Deduped so POTA
   * and SOTA pollers can't stack two warnings.
   */
  function spotsConnectivityToast() {
    dismissPreviousToast('spotsConnectivity')
    const toastId = toast.warning(
      "Can't reach the Spots server. Spots may be out of date - check your connection.",
      { ...baseOptions, timeout: false }
    )
    activeToastIds.value.spotsConnectivity = toastId
  }

  /** Dismiss the spot connectivity warning once spots are fetching again. */
  function dismissSpotsConnectivityToast() {
    dismissPreviousToast('spotsConnectivity')
  }

  /**
   * Notify that a POTA spot matched the user's watchlist. Debouncing lives in
   * WatchlistStore; this just renders the match. Auto-dismisses like other
   * informational spot toasts.
   */
  function watchlistMatchToast(
    spot: PotaSpotType | SotaSpotType,
    matchedCallsign: string | null,
    matchedRef: string | null,
    refKind: WatchlistRefKind = 'park'
  ) {
    const content = {
      component: WatchlistMatchToast,
      props: {
        spot,
        matchedCallsign,
        matchedRef,
        refLabel: refKind
      }
    }
    toast.info(content, baseOptions)
  }

  /** Confirm a successful rig control connection (e.g. from the config panel). */
  function rigControlConnectedToast(radio?: string) {
    dismissPreviousToast('rigControlStatus')
    activeToastIds.value.rigControlStatus = toast.success(
      radio ? `Connected to ${radio}.` : 'Rig control connected.',
      baseOptions
    )
  }

  /** Confirm that rig control came back after an auto-reconnect. */
  function rigControlReconnectedToast() {
    dismissPreviousToast('rigControlStatus')
    activeToastIds.value.rigControlStatus = toast.success('Rig control reconnected.', baseOptions)
  }

  /** Let the user know auto-reconnect gave up (info uses a readable blue toast). */
  function rigControlReconnectFailedToast() {
    dismissPreviousToast('rigControlStatus')
    activeToastIds.value.rigControlStatus = toast.info(
      'Rig control stopped trying to reconnect. Re-test it in Settings once your radio is ready.',
      baseOptions
    )
  }

  return {
    callsignFoundToast,
    callsignNotFoundToast,
    duplicateQsoToast,
    lookupProviderErrorToast,
    potaParkFoundToast,
    potaSpotCreatedToast,
    sotaSummitFoundToast,
    adifExportErrorToast,
    adifExportSuccessToast,
    adifExportPartialToast,
    adifExportCancelledToast,
    cabrilloExportErrorToast,
    syncErrorToast,
    spotsConnectivityToast,
    dismissSpotsConnectivityToast,
    watchlistMatchToast,
    rigControlConnectedToast,
    rigControlDisconnectedToast,
    rigControlReconnectedToast,
    rigControlReconnectFailedToast
  }
}
